import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { expensasApi } from '../../lib/api';
import { ExpensasEstadoCuenta, ExpensasResumenDeuda } from '../../components/propietario/ExpensasEstadoCuenta';

const fmtMoney = (n) => `$ ${Math.round(Number(n || 0)).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
const fmtPeriodo = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

const TABS = [
  { id: 'detalle', label: 'Detalle' },
  { id: 'pagos', label: 'Pagos' },
  { id: 'estado_cuenta', label: 'Estado de cuenta' },
];

export default function InquilinoUnidadDetalle() {
  const { unidadId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('detalle');
  const [liqs, setLiqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nombre, setNombre] = useState('');

  useEffect(() => {
    expensasApi.misLiquidaciones()
      .then((r) => {
        const all = r.data?.liquidaciones || [];
        const mine = all.filter((l) => l.unidad_id === unidadId);
        setLiqs(mine);
        if (mine[0]) setNombre(mine[0].unidad_nombre);
      })
      .catch(() => setLiqs([]))
      .finally(() => setLoading(false));
  }, [unidadId]);

  return (
    <div data-testid="inquilino-unidad-detalle">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-3 md:px-8 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/inquilino/expensas')} className="text-slate-400 hover:text-slate-700" data-testid="inq-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-slate-900">Expensas — {nombre || 'Unidad'}</h1>
        </div>
        <div className="max-w-5xl mx-auto px-3 md:px-8">
          <div className="flex gap-0 -mb-px">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`inq-tab-${t.id}`}
                className={cn(
                  'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  activeTab === t.id ? 'border-[#1B4D3E] text-[#1B4D3E]' : 'border-transparent text-slate-500 hover:text-slate-700'
                )}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 md:px-8 py-4">
        {activeTab === 'estado_cuenta' && <ExpensasResumenDeuda unidadId={unidadId} />}
        {activeTab === 'pagos' && <ExpensasEstadoCuenta unidadId={unidadId} />}
        {activeTab === 'detalle' && (
          loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : liqs.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
              Aún no hay liquidaciones de expensas a tu cargo para esta unidad.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium">Período</th>
                    <th className="py-2 px-3 text-right font-medium">Expensas</th>
                    <th className="py-2 px-3 text-right font-medium">Total</th>
                    <th className="py-2 px-3 text-right font-medium">Vto 1</th>
                    <th className="py-2 px-3 text-right font-medium">Vto 2</th>
                    <th className="py-2 px-3 text-right font-medium">Vto 3</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {liqs.map((l) => (
                    <tr key={l.liquidacion_id} data-testid={`inq-liq-${l.liquidacion_id}`}>
                      <td className="py-2 px-3 font-medium text-slate-900">{fmtPeriodo(l.periodo)}</td>
                      <td className="py-2 px-3 text-right font-mono">{fmtMoney(l.monto_expensas)}</td>
                      <td className="py-2 px-3 text-right font-mono font-bold text-slate-900">{fmtMoney(l.total)}</td>
                      <td className="py-2 px-3 text-right text-xs font-mono text-slate-500">{fmtMoney(l.vto1_total)}</td>
                      <td className="py-2 px-3 text-right text-xs font-mono text-slate-500">{fmtMoney(l.vto2_total)}</td>
                      <td className="py-2 px-3 text-right text-xs font-mono text-slate-500">{fmtMoney(l.vto3_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}
